import {QUESTIONIDS} from './../types/question';
import {
  SurveyResponse,
  VWCarModel,
  ResultsSchema,
} from '../types/surveyResponse';
import lzString from 'lz-string';
import {BodyThree, BodyTwo, ResultMap, SectionOne} from '../types/resultMap';

import {config} from '../environment';

export function generateResultsUrl(
  surveyResponse: SurveyResponse,
  pathName: string,
  search: string,
) {
  const {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    [QUESTIONIDS.questionTwoYourVehicle]: _,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    [QUESTIONIDS.questionThirteenForm]: __,
    ...rest
  } = surveyResponse;

  const pathSegments = pathName.split('/');
  pathSegments.pop();
  const surveyUrl = pathSegments.join('/');
  const existingQueryStrings = new URLSearchParams(search);
  existingQueryStrings.set(
    'schema',
    lzString.compressToEncodedURIComponent(JSON.stringify(rest)),
  );

  return {
    pathname: `${surveyUrl}/results`,
    search: existingQueryStrings.toString(),
  };
}

export function generateSurveyUrl(
  surveyResponse: SurveyResponse,
  url?: string,
) {
  const {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    [QUESTIONIDS.questionTwoYourVehicle]: _,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    [QUESTIONIDS.questionThirteenForm]: __,
    ...rest
  } = surveyResponse;

  if (!url) return '';
  return (
    url.replace('start', 'results') +
    '?schema=' +
    lzString.compressToEncodedURIComponent(JSON.stringify(rest))
  );
}

export function generateCarModel(surveyResponse: ResultsSchema): VWCarModel {
  if (surveyResponse.q3_vehicle_use === 'transporting_goods') {
    return 'idBuzzCargo';
  }
  if (surveyResponse.q3_vehicle_use === 'off_roading') {
    return 'amarok';
  }
  if (surveyResponse.q3_vehicle_use === 'exploring') {
    return 'touareg';
  }
  if (surveyResponse.q3_vehicle_use === 'towing') {
    return 'amarok';
  }
  if (surveyResponse.q3_vehicle_use === 'trade_tools') {
    if (surveyResponse.q6_fits_your_trade === 'van') {
      return 'idBuzzCargo';
    }
    return 'amarok';
  }
  if (
    surveyResponse.q3_vehicle_use === 'family_car' ||
    surveyResponse.q3_vehicle_use === 'road_trips' ||
    surveyResponse.q3_vehicle_use === 'ride_share' ||
    surveyResponse.q3_vehicle_use === 'joy_driving' ||
    surveyResponse.q3_vehicle_use === 'daily_commuting'
  ) {
    if (surveyResponse.q4_passengers >= 6) {
      return 'idBuzz7';
    }
    if (surveyResponse.q7_new_vehicle_features.includes('retro_styling')) {
      return 'idBuzz5';
    }
    if (surveyResponse.q7_new_vehicle_features.includes('touring_ability')) {
      return 'touareg';
    }
    if (
      surveyResponse.q7_new_vehicle_features.includes('sporty_styling') &&
      surveyResponse.q7_new_vehicle_features.includes('performance_and_awd')
    ) {
      return 'id5gtx';
    }
    if (surveyResponse.q7_new_vehicle_features.includes('sporty_styling')) {
      return 'id5';
    }
    if (
      surveyResponse.q7_new_vehicle_features.includes(
        'versatility_and_carrying_capacity',
      ) &&
      surveyResponse.q7_new_vehicle_features.includes('performance_and_awd')
    ) {
      return 'id4gtx';
    }
    if (
      surveyResponse.q7_new_vehicle_features.includes(
        'versatility_and_carrying_capacity',
      )
    ) {
      return 'id4';
    }
    if (
      surveyResponse.q7_new_vehicle_features.includes('performance_and_awd')
    ) {
      return 'id4gtxAndid5gtx';
    }

    const finalResponses: typeof surveyResponse.q7_new_vehicle_features = [
      'driving_performance',
      'technology_and_infotainment',
      'latest_safety_features',
      'savings_on_fuel_and_affordability',
      'sustainability',
      'comfort_and_styling',
    ];
    for (const response of finalResponses) {
      if (surveyResponse.q7_new_vehicle_features.includes(response)) {
        return 'id4Andid5';
      }
    }
  }

  throw new Error('Invalid survey response');
}

export function generateCarModelName(surveyResponse: ResultsSchema): string {
  const id = generateCarModel(surveyResponse);

  return generateCarModelNameFromId(id) || id;
}

export function generateCarModelNameFromId(id: VWCarModel): string {
  const carModelNames: Partial<Record<VWCarModel, string>> = {
    id4: 'ID.4-PRO',
    id4gtx: 'ID.4-GTX',
    id5: 'ID.5-PRO',
    id5gtx: 'ID.5-GTX',
    idBuzz5: 'ID.BUZZ-SWB',
    idBuzz7: 'ID.BUZZ-LWB',
    idBuzzCargo: 'ID.BUZZ-CARGO-SWB',
    touareg: 'TOUAREG-R-EHYBRID',
  };

  return carModelNames[id] || id;
}

export function generateSectionOneData(
  surveyResponse: ResultsSchema,
  responseData: ResultMap,
) {
  const car = generateCarModel(surveyResponse);
  return {
    ...responseData.section_one[car],
    name: surveyResponse.q1_name,
    assetUrl: config.EV_FIT_ASSETS_BASE_URL,
  };
}

export function generateSectionOneCombinedData(
  surveyResponse: ResultsSchema,
  responseData: ResultMap,
) {
  const car = generateCarModel(surveyResponse);
  const [carOne, CarTwo] = car.split('And');
  if (!carOne || !CarTwo) throw new Error('Incorrect Data at car split');
  return {
    cars: [
      {...responseData.section_one[carOne as keyof SectionOne]},
      {...responseData.section_one[CarTwo as keyof SectionOne]},
    ],
    hero_title: responseData.section_one[car].hero_title,
    hero_image_background: responseData.section_one[car].hero_image_background,
    name: surveyResponse.q1_name,
    assetUrl: config.EV_FIT_ASSETS_BASE_URL,
  };
}

export function generateSectionTwoData(
  surveyResponse: ResultsSchema,
  responseData: ResultMap,
) {
  const isHomeCharging =
    surveyResponse.q10_charging_home === 'yes' ||
    surveyResponse.q11_charging_work === 'yes';

  const image = isHomeCharging
    ? config.EV_FIT_ASSETS_BASE_URL +
      responseData.section_two.img.img_charger_home
    : config.EV_FIT_ASSETS_BASE_URL +
      responseData.section_two.img.img_charger_map;

  const image_background = isHomeCharging
    ? config.EV_FIT_ASSETS_BASE_URL +
      responseData.section_two.img_background.img_charger_home
    : config.EV_FIT_ASSETS_BASE_URL +
      responseData.section_two.img_background.img_charger_map;

  const heroSubTitle = isHomeCharging
    ? responseData.section_two.hero_sub_title.txt_charger_home
    : responseData.section_two.hero_sub_title.txt_charger_map;

  const heroTitleLineOne = isHomeCharging
    ? responseData.section_two.hero_title_line_1.txt_charger_home
    : responseData.section_two.hero_title_line_1.txt_charger_map;

  const ctaPrimary = isHomeCharging
    ? responseData.section_two.cta.charging_home
    : responseData.section_two.cta.charging_map;

  const ctaSecondary = isHomeCharging
    ? responseData.section_two.cta.charging_home
    : responseData.section_two.cta.charging_map;

  const heroTitleLineTwo =
    surveyResponse.q10_charging_home === 'yes'
      ? responseData.section_two.hero_title_line_2.txt_charger_home
      : responseData.section_two.hero_title_line_2.txt_charger_map;

  const body = isHomeCharging
    ? responseData.section_two.body.default
    : responseData.section_two.body.map;

  const bodyTwo = (surveyResponse: SurveyResponse) => {
    if (isHomeCharging) {
      const {q9_charging_type} = surveyResponse;

      if (!Array.isArray(q9_charging_type)) {
        return undefined;
      }

      return q9_charging_type
        .filter((value) => value !== 'none')
        .map(
          (value) => responseData.section_two.body_two[value as keyof BodyTwo],
        )
        .join(' ');
    }
    return undefined;
  };

  const bodyThree = (surveyResponse: SurveyResponse) => {
    if (isHomeCharging) {
      const {q3_vehicle_use} = surveyResponse;
      const keys = Object.keys(responseData.section_two.body_three);
      if (q3_vehicle_use && keys.includes(q3_vehicle_use)) {
        return responseData.section_two.body_three[
          q3_vehicle_use as keyof BodyThree
        ];
      }
      return responseData.section_two.body_three['default'];
    }
    return undefined;
  };

  const bodyTwoContent = bodyTwo(surveyResponse as SurveyResponse);

  return {
    name: surveyResponse.q1_name,
    model: generateCarModel(surveyResponse),
    image: image,
    image_background: image_background,
    hero_sub_title: heroSubTitle,
    hero_title_line_1: heroTitleLineOne,
    hero_title_line_2: heroTitleLineTwo,
    body: body,
    ...(bodyTwoContent && {body_two: bodyTwoContent}),
    body_three: bodyThree(surveyResponse as SurveyResponse),
    cta_one: ctaPrimary,
    cta_two: ctaSecondary,
    post_code: surveyResponse?.q8_address?.postcode || '',
    isHomeCharging,
  };
}

export function generateSectionThreeData(
  surveyResponse: ResultsSchema,
  responseData: ResultMap,
) {
  const {q3_vehicle_use} = surveyResponse;
  const car = generateCarModel(surveyResponse);
  return {
    name: surveyResponse.q1_name,
    model: car,
    ...(responseData.section_three[car][
      q3_vehicle_use as keyof (typeof responseData.section_three)[typeof car]
    ] ?? responseData.section_three[car]['default']),
    assetUrl: config.EV_FIT_ASSETS_BASE_URL,
  };
}

export function generateSectionFourData(
  surveyResponse: ResultsSchema,
  responseData: ResultMap,
) {
  const car = generateCarModel(surveyResponse);
  type SurveyResponse_q7 = Omit<
    (typeof surveyResponse.q7_new_vehicle_features)[number],
    'default'
  >;

  const keys = surveyResponse.q7_new_vehicle_features;
  type Keys = keyof (typeof responseData.section_four)[typeof car];
  const sectionFourVariantsByPreference: Record<
    VWCarModel,
    Array<SurveyResponse_q7>
  > = {
    id4: [
      'comfort_and_styling',
      'technology_and_infotainment',
      'latest_safety_features',
      'savings_on_fuel_and_affordability',
      'versatility_and_carrying_capacity',
      'sustainability',
    ],
    id4gtx: [
      'performance_and_awd',
      'comfort_and_styling',
      'technology_and_infotainment',
      'latest_safety_features',
      'savings_on_fuel_and_affordability',
      'versatility_and_carrying_capacity',
      'sustainability',
    ],
    id5: [
      'sporty_styling',
      'comfort_and_styling',
      'technology_and_infotainment',
      'latest_safety_features',
      'savings_on_fuel_and_affordability',
      'versatility_and_carrying_capacity',
      'sustainability',
    ],
    id5gtx: [
      'sporty_styling',
      'performance_and_awd',
      'comfort_and_styling',
      'technology_and_infotainment',
      'latest_safety_features',
      'savings_on_fuel_and_affordability',
      'versatility_and_carrying_capacity',
      'sustainability',
    ],
    idBuzz5: [
      'retro_styling',
      'comfort_and_styling',
      'technology_and_infotainment',
      'latest_safety_features',
      'savings_on_fuel_and_affordability',
      'versatility_and_carrying_capacity',
      'sustainability',
    ],
    idBuzz7: [
      'retro_styling',
      'comfort_and_styling',
      'technology_and_infotainment',
      'latest_safety_features',
      'savings_on_fuel_and_affordability',
      'versatility_and_carrying_capacity',
      'sustainability',
    ],
    idBuzzCargo: [
      'performance_and_awd',
      'comfort_and_styling',
      'technology_and_infotainment',
      'latest_safety_features',
      'savings_on_fuel_and_affordability',
      'versatility_and_carrying_capacity',
      'sustainability',
    ],
    amarok: [
      'comfort_and_styling',
      'technology_and_infotainment',
      'latest_safety_features',
      'versatility_and_carrying_capacity',
      'towing_ability',
    ],
    touareg: [
      'touring_ability',
      'sporty_styling',
      'performance_and_awd',
      'comfort_and_styling',
      'technology_and_infotainment',
      'latest_safety_features',
      'savings_on_fuel_and_affordability',
      'versatility_and_carrying_capacity',
      'sustainability',
    ],
    id4Andid5: [
      'sporty_styling',
      'comfort_and_styling',
      'technology_and_infotainment',
      'latest_safety_features',
      'savings_on_fuel_and_affordability',
      'versatility_and_carrying_capacity',
      'sustainability',
    ],
    id4gtxAndid5gtx: [
      'sporty_styling',
      'performance_and_awd',
      'comfort_and_styling',
      'comfort_and_styling',
      'technology_and_infotainment',
      'latest_safety_features',
      'savings_on_fuel_and_affordability',
      'versatility_and_carrying_capacity',
      'sustainability',
    ],
  };
  if (Array.isArray(keys) && keys.length > 0) {
    for (const key of keys as Keys[]) {
      if (sectionFourVariantsByPreference[car].includes(key)) {
        console.log(key);
        return {
          model: car,
          ...responseData.section_four[car][key],
          assetUrl: config.EV_FIT_ASSETS_BASE_URL,
        };
      }
    }
    return {
      model: car,
      ...responseData.section_four[car]['default'],
      assetUrl: config.EV_FIT_ASSETS_BASE_URL,
    };
  }
  return {
    model: car,
    ...responseData.section_four[car]['default'],
    assetUrl: config.EV_FIT_ASSETS_BASE_URL,
  };
}

export function generateSectionFiveData(
  surveyResponse: ResultsSchema,
  responseData: ResultMap,
) {
  const car = generateCarModel(surveyResponse);
  if (car === 'amarok') {
    return {
      model: car,
      ...responseData.section_five.amarok,
      assetUrl: config.EV_FIT_ASSETS_BASE_URL,
    };
  }
  return {
    model: car,
    ...responseData.section_five.default,
    assetUrl: config.EV_FIT_ASSETS_BASE_URL,
  };
}
